// src/components/modal/MenuItemDetails/utils/sessionUtils.ts
import type { MenuItem } from "~/types/new_types";
import type { ItemCard } from "../types";
import { getItemName } from "~/utils/menuItemHelper";

/**
 * Loads existing item cards from session storage
 */
export function loadItemCardsFromSession(
  item: MenuItem,
  language: string
): ItemCard[] {
  try {
    const savedItemCards = sessionStorage.getItem("restaurant-item-cards");
    if (!savedItemCards) return [];

    const itemCardsData = JSON.parse(savedItemCards);
    const existingItemData = itemCardsData[item.id];

    if (!existingItemData) return [];

    // Check for new cards structure first
    if (existingItemData.cards && existingItemData.cards.length > 0) {
      // Build a lookup for variant groups to check is_multiselection
      // Index by multiple keys (id, form_name, lowercase name) for robust matching
      const variantGroupLookup = new Map<string, { groupId: string; isMulti: boolean }>();
      if (item.variants_and_options && Array.isArray(item.variants_and_options)) {
        item.variants_and_options.forEach((group: any) => {
          const groupId = group.id || group.form_name;
          const formName = group.form_name || '';
          const groupName = group.name || '';
          const isMulti = Boolean(group.is_multiselection);
          
          if (groupId) {
            // Index by exact ID
            variantGroupLookup.set(groupId, { groupId, isMulti });
            // Also index by form_name if different
            if (formName && formName !== groupId) {
              variantGroupLookup.set(formName, { groupId, isMulti });
            }
            // Also index by lowercase for case-insensitive fallback
            const lowerFormName = formName.toLowerCase().trim();
            const lowerGroupName = groupName.toLowerCase().trim();
            if (lowerFormName) {
              variantGroupLookup.set(lowerFormName, { groupId, isMulti });
            }
            if (lowerGroupName && lowerGroupName !== lowerFormName) {
              variantGroupLookup.set(lowerGroupName, { groupId, isMulti });
            }
          }
        });
      }

      // Helper function to find matching group
      const findGroupInfo = (variantKey: string, variantName: string): { groupId: string; isMulti: boolean } | null => {
        // Try exact key match first
        if (variantGroupLookup.has(variantKey)) {
          return variantGroupLookup.get(variantKey)!;
        }
        // Try lowercase key
        const lowerKey = variantKey.toLowerCase().trim();
        if (variantGroupLookup.has(lowerKey)) {
          return variantGroupLookup.get(lowerKey)!;
        }
        // Try exact name
        if (variantName && variantGroupLookup.has(variantName)) {
          return variantGroupLookup.get(variantName)!;
        }
        // Try lowercase name
        const lowerName = (variantName || '').toLowerCase().trim();
        if (lowerName && variantGroupLookup.has(lowerName)) {
          return variantGroupLookup.get(lowerName)!;
        }
        return null;
      };

      return existingItemData.cards.map((cardData: any, index: number) => {
        const cardVariants: Record<string, any> = {};

        if (cardData.variants && cardData.variants.length > 0) {
          cardData.variants.forEach((variant: any) => {
            if (
              variant &&
              (variant.name || variant.key) &&
              Array.isArray(variant.children) &&
              variant.children.length > 0
            ) {
              const variantKey = variant.key || variant.name;
              const variantName = variant.name || '';
              if (!variantKey) return;

              // Find the matching group to get the actual groupId and multiselection info
              const groupInfo = findGroupInfo(variantKey, variantName);
              const actualGroupId = groupInfo?.groupId || variantKey;
              const isMultiSelection = groupInfo?.isMulti ?? false;

              // Check if this is a combo/upgrade variant (has comboData)
              const isComboVariant = variant.actionType === 'upgrade' || 
                variant.variantType === 'meals_and_combos' ||
                variant.children.some((c: any) => c.comboData);

              if (isComboVariant) {
                // Handle combo variants
                if (variant.children.length === 1) {
                  const child = variant.children[0];
                  if (child.comboData) {
                    cardVariants[actualGroupId] = {
                      comboName: child.name || variant.name,
                      items: child.comboData?.items || child.includedItems || [],
                      price: child.price || child.comboData?.combo_price || 0,
                    };
                  } else {
                    cardVariants[actualGroupId] = child.name;
                  }
                } else {
                  cardVariants[actualGroupId] = variant.children.map((c: any) => ({
                    comboName: c.name,
                    items: c.comboData?.items || c.includedItems || [],
                    price: c.price || c.comboData?.combo_price || 0,
                  }));
                }
              } else if (isMultiSelection) {
                // Multi-selection: ALWAYS store as array
                cardVariants[actualGroupId] = variant.children.map((c: any) => c.name);
              } else {
                // Single selection: store as string
                if (variant.children.length === 1) {
                  cardVariants[actualGroupId] = variant.children[0].name;
                } else {
                  // If somehow there are multiple children for a single-select, just take the first
                  cardVariants[actualGroupId] = variant.children[0].name;
                }
              }
            }
          });
        }

        return {
          id: cardData.cardId || `${item.id}-${Date.now()}-${index + 1}`,
          name: cardData.cardName || `${getItemName(item, language)} #${index + 1}`,
          variants: cardVariants,
          quantity: cardData.quantity || 1,
        };
      });
    }

    // Fallback to old variants structure for backward compatibility
    if (existingItemData.variants) {
      const reconstructedCards: ItemCard[] = [];
      const totalQuantity = existingItemData.totalQuantity || 0;

      if (totalQuantity > 0) {
        const hasActualVariants = existingItemData.variants.some(
          (v: any) => v.name && v.children.length > 0
        );

        if (hasActualVariants) {
          const uniqueConfigurations = new Map();

          existingItemData.variants.forEach((variant: any) => {
            const variantIdentifier = variant?.key || variant?.name;
            if (variantIdentifier && variant.children.length > 0) {
              const configKey = `${variantIdentifier}:${variant.children
                .map((c: any) => c.name)
                .join(",")}`;
              if (!uniqueConfigurations.has(configKey)) {
                uniqueConfigurations.set(configKey, variant);
              }
            }
          });

          const cardVariants: Record<string, any> = {};
          uniqueConfigurations.forEach((variant) => {
            if (!variant) return;
            const variantKey = variant.key || variant.name;
            if (!variantKey) return;

            if (variant.children.length === 1) {
              cardVariants[variantKey] = variant.children[0].name;
            } else {
              cardVariants[variantKey] = variant.children.map((c: any) => c.name);
            }
          });

          for (let i = 0; i < totalQuantity; i++) {
            reconstructedCards.push({
              id: `${item.id}-${Date.now()}-${i + 1}`,
              name: `${getItemName(item, language)} #${i + 1}`,
              variants: cardVariants,
              quantity: 1,
            });
          }
        } else {
          for (let i = 0; i < totalQuantity; i++) {
            reconstructedCards.push({
              id: `${item.id}-${Date.now()}-${i + 1}`,
              name: `${getItemName(item, language)} #${i + 1}`,
              variants: {},
              quantity: 1,
            });
          }
        }
      }

      return reconstructedCards;
    }

    return [];
  } catch (error) {
    console.error("Error loading existing item cards:", error);
    return [];
  }
}

/**
 * Removes an item from session storage
 */
export function removeItemFromSession(itemId: string): void {
  try {
    const existingData = sessionStorage.getItem("restaurant-item-cards");
    const currentData = existingData ? JSON.parse(existingData) : {};
    delete currentData[itemId];

    if (Object.keys(currentData).length > 0) {
      sessionStorage.setItem("restaurant-item-cards", JSON.stringify(currentData));
    } else {
      sessionStorage.removeItem("restaurant-item-cards");
    }

    // Trigger storage event
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: "restaurant-item-cards",
        newValue:
          Object.keys(currentData).length > 0
            ? JSON.stringify(currentData)
            : null,
        storageArea: sessionStorage,
      })
    );
  } catch (error) {
    console.error("Error removing item from session storage:", error);
  }
}

/**
 * Saves item cards data to session storage
 */
export function saveItemCardsToSession(itemId: string, itemData: any): void {
  try {
    const existingData = sessionStorage.getItem("restaurant-item-cards");
    const currentData = existingData ? JSON.parse(existingData) : {};

    currentData[itemId] = {
      ...currentData[itemId],
      ...itemData,
    };

    sessionStorage.setItem("restaurant-item-cards", JSON.stringify(currentData));

    // Trigger storage event
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: "restaurant-item-cards",
        newValue: JSON.stringify(currentData),
        storageArea: sessionStorage,
      })
    );
  } catch (error) {
    console.error("Error saving item cards to session storage:", error);
  }
}
